import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/contexts/AuthContext';

/**
 * Custom hook for checking training-specific quota limits
 * Provides methods to check if user can perform training operations
 */
export const useTrainingQuota = () => {
  const { subscription, usage } = useAuth();
  const { t } = useTranslation('pricing');

  const quotaInfo = useMemo(() => {
    if (!subscription || !usage) {
      return {
        canUpload: false,
        canProcessDataset: false,
        canTrainModel: false,
        storageRemaining: 0,
        processingRemaining: 0,
        trainingRemaining: 0,
        storagePercentage: 100,
        processingPercentage: 100,
        trainingPercentage: 100,
      };
    }

    // Compute-hours plan: gate by compute time, not job count.
    // Matches QuotaCounter.tsx:93-96 and useQuotaCheck.ts:47-51.
    const usesComputeHours =
      subscription.total_compute_hours !== null &&
      subscription.total_compute_hours !== undefined &&
      subscription.total_compute_hours > 0;

    // Storage quota (same for both plan types)
    const storageLimit = subscription.max_storage_gb || 0;
    const storageUsed = usage.storage_used_gb || 0;
    const storageRemaining = Math.max(0, storageLimit - storageUsed);
    const storagePercentage = storageLimit > 0 ? (storageUsed / storageLimit) * 100 : 100;
    const canUpload = storageRemaining > 0;

    if (usesComputeHours) {
      const limitSec = (subscription.total_compute_hours ?? 0) * 3600;
      const usedSec = usage.total_compute_seconds ?? 0;
      const hasHoursLeft = usedSec < limitSec;
      const computePercentage = limitSec > 0 ? (usedSec / limitSec) * 100 : 100;

      return {
        canUpload,
        canProcessDataset: hasHoursLeft,
        canTrainModel: hasHoursLeft && (subscription.can_use_training || false),
        storageRemaining,
        processingRemaining: hasHoursLeft ? Infinity : 0,
        trainingRemaining: hasHoursLeft ? Infinity : 0,
        storagePercentage,
        processingPercentage: computePercentage,
        trainingPercentage: computePercentage,
        trainingAllowed: subscription.can_use_training || false,
      };
    }

    // Monthly plan: existing logic
    const processingLimit = subscription.max_processing_jobs_per_month || 0;
    const processingUsed = usage.processing_jobs_count || 0;
    const processingRemaining = Math.max(0, processingLimit - processingUsed);
    const processingPercentage = processingLimit > 0 ? (processingUsed / processingLimit) * 100 : 100;
    const canProcessDataset = processingRemaining > 0;

    // Training quota
    const trainingAllowed = subscription.can_use_training || false;
    const trainingLimit = subscription.max_training_runs_per_month || 0;
    const trainingUsed = usage.training_runs_count || 0;
    const trainingRemaining = Math.max(0, trainingLimit - trainingUsed);
    const trainingPercentage = trainingLimit > 0 ? (trainingUsed / trainingLimit) * 100 : 100;
    const canTrainModel = trainingAllowed && (trainingLimit === -1 || trainingRemaining > 0);

    return {
      canUpload,
      canProcessDataset,
      canTrainModel,
      storageRemaining,
      processingRemaining,
      trainingRemaining,
      storagePercentage,
      processingPercentage,
      trainingPercentage,
      trainingAllowed,
    };
  }, [subscription, usage]);

  /**
   * Get detailed error message for quota exceeded
   */
  const getQuotaError = (operationType: 'upload' | 'processing' | 'training'): string => {
    if (!subscription || !usage) {
      return t('quota.unavailableCheck');
    }

    // Compute-hours plans gate processing/training by compute seconds, not job
    // counts — surface the compute-exhausted message instead of "(X/Y jobs)".
    const usesComputeHours =
      subscription.total_compute_hours !== null &&
      subscription.total_compute_hours !== undefined &&
      subscription.total_compute_hours > 0;

    switch (operationType) {
      case 'upload':
        return t('quota.storageExceeded', {
          used: usage.storage_used_gb?.toFixed(2),
          limit: subscription.max_storage_gb,
        });

      case 'processing':
        if (usesComputeHours) {
          return t('quota.computeExhausted');
        }
        return t('quota.processingExceeded', {
          used: usage.processing_jobs_count,
          limit: subscription.max_processing_jobs_per_month,
        });

      case 'training':
        if (!subscription.can_use_training) {
          return t('quota.trainingUnavailable');
        }
        if (usesComputeHours) {
          return t('quota.computeExhausted');
        }
        return t('quota.trainingExceeded', {
          used: usage.training_runs_count,
          limit: subscription.max_training_runs_per_month,
        });

      default:
        return t('quota.computeExhausted');
    }
  };

  /**
   * Check if user can perform specific operation
   */
  const checkOperation = (operationType: 'upload' | 'processing' | 'training'): { allowed: boolean; error?: string } => {
    switch (operationType) {
      case 'upload':
        return {
          allowed: quotaInfo.canUpload,
          error: quotaInfo.canUpload ? undefined : getQuotaError('upload'),
        };

      case 'processing':
        return {
          allowed: quotaInfo.canProcessDataset,
          error: quotaInfo.canProcessDataset ? undefined : getQuotaError('processing'),
        };

      case 'training':
        return {
          allowed: quotaInfo.canTrainModel,
          error: quotaInfo.canTrainModel ? undefined : getQuotaError('training'),
        };

      default:
        return { allowed: false, error: 'Invalid operation type' };
    }
  };

  return {
    ...quotaInfo,
    getQuotaError,
    checkOperation,
    subscription,
    usage,
  };
};

export default useTrainingQuota;
